import type {
  TruckSpec,
  ItemDimensions,
  FitResult,
  LoadingType,
} from "./types";
import { isCylindrical } from "./types";
import { planFloor, type FloorSlot } from "./floor-layout";

const ZERO_RESULT: FitResult = {
  floorCount: 0,
  stackLayers: 0,
  totalBySpace: 0,
  totalByWeight: 0,
  totalFits: 0,
  limitingFactor: "space",
  spaceUtilization: 0,
  weightUtilization: 0,
};

function hasDimensions(item: ItemDimensions): boolean {
  return item.lengthIn > 0 && item.widthIn > 0 && item.heightIn > 0;
}

export function floorSlotsFor(
  truck: TruckSpec,
  item: ItemDimensions,
  loadingType: LoadingType,
): Array<FloorSlot> {
  if (!isCylindrical(item)) return planFloor(truck, item, loadingType);
  const diameterIn = Math.max(item.lengthIn, item.widthIn);
  return planFloor(
    truck,
    { lengthIn: diameterIn, widthIn: diameterIn },
    "straight",
  );
}

export function layerHeightIn(item: ItemDimensions): number {
  return item.heightIn + (item.palletized ? item.palletHeightIn : 0);
}

export function stackLayersFor(truck: TruckSpec, item: ItemDimensions): number {
  const layersThatFit = Math.floor(truck.heightIn / layerHeightIn(item));
  return item.stackable ? layersThatFit : Math.min(layersThatFit, 1);
}

function footprintAreaSqIn(item: ItemDimensions): number {
  if (!isCylindrical(item)) return item.lengthIn * item.widthIn;
  const diameterIn = Math.max(item.lengthIn, item.widthIn);
  return Math.PI * (diameterIn / 2) ** 2;
}

function percentOf(part: number, whole: number): number {
  return whole > 0 ? Math.min((part / whole) * 100, 100) : 0;
}

export function calculateFit(
  truck: TruckSpec,
  item: ItemDimensions,
  loadingType: LoadingType = "straight",
): FitResult {
  if (!hasDimensions(item)) return { ...ZERO_RESULT };

  const floorCount = floorSlotsFor(truck, item, loadingType).length;
  const stackLayers = stackLayersFor(truck, item);
  const totalBySpace = floorCount * stackLayers;
  const totalByWeight =
    item.weightLbs > 0
      ? Math.floor(truck.maxWeightLbs / item.weightLbs)
      : totalBySpace;
  const totalFits = Math.min(totalBySpace, totalByWeight);

  const usedVolume =
    floorCount * footprintAreaSqIn(item) * stackLayers * layerHeightIn(item);
  const truckVolume = truck.lengthIn * truck.widthIn * truck.heightIn;

  return {
    floorCount,
    stackLayers,
    totalBySpace,
    totalByWeight,
    totalFits,
    limitingFactor: totalByWeight < totalBySpace ? "weight" : "space",
    spaceUtilization: percentOf(usedVolume, truckVolume),
    weightUtilization:
      item.weightLbs > 0
        ? percentOf(totalFits * item.weightLbs, truck.maxWeightLbs)
        : 0,
  };
}

const ALL_LOADING_TYPES: Array<LoadingType> = [
  "straight",
  "turned",
  "pinwheel",
];

export function findBestLoadingType(
  truck: TruckSpec,
  item: ItemDimensions,
): LoadingType {
  let bestType: LoadingType = "straight";
  let bestCount = 0;

  for (const type of ALL_LOADING_TYPES) {
    const result = calculateFit(truck, item, type);
    if (result.totalFits > bestCount) {
      bestCount = result.totalFits;
      bestType = type;
    }
  }

  return bestType;
}
